import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Wand2, RefreshCw, History, Layers, Trash2, Search, ChevronDown, Target, Lock, Maximize2 } from 'lucide-react';
import Button from '../ui/Button';
import SymbolSearch from '../ui/SymbolSearch';
import UpgradePrompt from '../ui/UpgradePrompt';
import { useShallow } from 'zustand/react/shallow';
import { useAnalysisStore } from '../../store/analysisStore';
import type { StoredAnalysisPayload } from '../../store/analysisStore';
import { useAuthStore } from '../../store/authStore';
import { marketService } from '../../services/market.service';
import { analysisService } from '../../services/analysis.service';
import { userPromptsService } from '../../services/userPrompts.service';
import { promptTemplatesService, type OfficialPromptTemplate } from '../../services/promptTemplates.service';
import { SUBSCRIPTION_PLANS, canUseCustomPrompts, formatDate, getSymbolName } from '@trading/shared';
import type { TradingSymbol, AnalysisMethodology, UserPrompt, UserPromptMethodology } from '@trading/shared';
import { logger } from '../../lib/logger.js';

// Map frontend methodology (kebab-case) to DB form (underscore) used by
// UserPrompt.methodology so we can filter the dropdown by current strategy.
const METHODOLOGY_TO_DB: Record<AnalysisMethodology, UserPromptMethodology> = {
  smc: 'smc',
  ict: 'ict',
  'price-action': 'price_action',
  fibonacci: 'fibonacci',
};

// Reverse map: DB strategy name → frontend methodology. Used when the user
// picks an official template from the prompt dropdown — we switch the
// active methodology to match the template they chose.
const DB_TO_METHODOLOGY: Record<string, AnalysisMethodology> = {
  smc: 'smc',
  ict: 'ict',
  price_action: 'price-action',
  fibonacci: 'fibonacci',
};

// Strategy keys for translation lookup
const STRATEGY_KEYS: Record<AnalysisMethodology, { nameKey: string; descKey: string }> = {
  smc: { nameKey: 'strategies.smc.name', descKey: 'strategies.smc.description' },
  ict: { nameKey: 'strategies.ict.name', descKey: 'strategies.ict.description' },
  'price-action': { nameKey: 'strategies.priceAction.name', descKey: 'strategies.priceAction.description' },
  fibonacci: { nameKey: 'strategies.fibonacci.name', descKey: 'strategies.fibonacci.description' },
};

const STRATEGY_ABBREV: Record<AnalysisMethodology, string> = {
  smc: 'SMC',
  ict: 'ICT',
  'price-action': 'PA',
  fibonacci: 'FIB',
};

const STRATEGY_LIST: AnalysisMethodology[] = ['smc', 'ict', 'price-action', 'fibonacci'];

import { ANALYSIS_CANDLE_MIN } from '@trading/shared';

// Local alias so the rest of the file reads naturally.
const MIN_CANDLES_FOR_ANALYSIS = ANALYSIS_CANDLE_MIN;

interface ChartToolbarProps {
  onAnalyze: () => void;
  onRefresh: () => void;
  isAnalyzing: boolean;
  isLoading: boolean;
  candleCount?: number;
  /** Count of candles before weekend filter was applied (from Chart-A backend PR). */
  candlesBeforeFilter?: number;
  showSMC?: boolean;
  onToggleSMC?: () => void;
  hasAnalysis?: boolean;
  onClearAnalysis?: () => void;
  analysisCompleted?: string; // Analysis ID to trigger history refresh when new analysis completes
  onFitContent?: () => void;
}

export default function ChartToolbar({
  onAnalyze,
  onRefresh,
  isAnalyzing,
  isLoading,
  candleCount = 0,
  candlesBeforeFilter,
  showSMC = true,
  onToggleSMC,
  hasAnalysis = false,
  onClearAnalysis,
  analysisCompleted,
  onFitContent,
}: ChartToolbarProps) {
  const { t } = useTranslation('analysis');
  const { symbol, timeframe, methodology, selectedUserPromptId, setSymbol, setTimeframe, setMethodology, setSelectedUserPromptId, setDebugProvider, loadAnalysis, clearAnalysis } = useAnalysisStore(
    useShallow((s) => ({
      symbol: s.symbol,
      timeframe: s.timeframe,
      methodology: s.methodology,
      selectedUserPromptId: s.selectedUserPromptId,
      setSymbol: s.setSymbol,
      setTimeframe: s.setTimeframe,
      setMethodology: s.setMethodology,
      setSelectedUserPromptId: s.setSelectedUserPromptId,
      setDebugProvider: s.setDebugProvider,
      loadAnalysis: s.loadAnalysis,
      clearAnalysis: s.clearAnalysis,
    }))
  );
  const user = useAuthStore((s) => s.user);
  const [currentSymbolInfo, setCurrentSymbolInfo] = useState<TradingSymbol | null>(null);
  const [recentAnalyses, setRecentAnalyses] = useState<StoredAnalysisPayload[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [pendingTimeframe, setPendingTimeframe] = useState<string | null>(null);
  const [showSymbolSearch, setShowSymbolSearch] = useState(false);
  const [showStrategySelector, setShowStrategySelector] = useState(false);
  const [userPrompts, setUserPrompts] = useState<UserPrompt[]>([]);
  const [officialTemplates, setOfficialTemplates] = useState<OfficialPromptTemplate[]>([]);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  // Plan-based restrictions
  const plan = user?.subscription?.plan || 'free';
  const planConfig = SUBSCRIPTION_PLANS[plan];
  const allowedTimeframes = planConfig.timeframes as readonly string[];
  const customPromptsUnlocked = canUseCustomPrompts(plan);

  // Global keyboard shortcut for symbol search (Ctrl+K or /)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Ctrl+K or Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSymbolSearch(true);
      }

      // / key (when not in input)
      if (e.key === '/' && !showSymbolSearch) {
        e.preventDefault();
        setShowSymbolSearch(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSymbolSearch]);

  // Load recent analyses on mount and when a new analysis completes
  useEffect(() => {
    analysisService.getHistory(1, 10).then(res => setRecentAnalyses(res.items)).catch((err) => logger.error('Failed to load recent analyses:', err));
  }, [analysisCompleted]);

  // Load custom prompts (Pro+) for the picker. Cleared/skipped on Free/Starter.
  useEffect(() => {
    if (!customPromptsUnlocked) {
      setUserPrompts([]);
      return;
    }
    userPromptsService.list().then(setUserPrompts).catch((err) => logger.error('Failed to load user prompts:', err));
  }, [customPromptsUnlocked]);

  // Load the official template list (any auth user). Used to render one
  // entry per methodology in the picker so the user sees the real options
  // instead of a single generic "official" item.
  useEffect(() => {
    promptTemplatesService.listOfficial().then(setOfficialTemplates).catch((err) => logger.error('Failed to load official templates:', err));
  }, []);

  // Auto-pick the methodology default if user has not explicitly chosen one
  // and the current selection no longer matches the active strategy bucket.
  useEffect(() => {
    if (!customPromptsUnlocked || userPrompts.length === 0) return;
    if (selectedUserPromptId) {
      const stillValid = userPrompts.find(
        (p) => p.id === selectedUserPromptId &&
          (p.methodology === null || p.methodology === METHODOLOGY_TO_DB[methodology]),
      );
      if (stillValid) return;
    }
    const dbMethodology = METHODOLOGY_TO_DB[methodology];
    const defaultPrompt = userPrompts.find(
      (p) => p.isDefault && (p.methodology === dbMethodology || p.methodology === null),
    );
    if (defaultPrompt) setSelectedUserPromptId(defaultPrompt.id);
  }, [customPromptsUnlocked, userPrompts, methodology, selectedUserPromptId, setSelectedUserPromptId]);

  // Drop selection when the user loses access (e.g. plan downgrade) so we
  // never accidentally send a userPromptId server-side.
  useEffect(() => {
    if (!customPromptsUnlocked && selectedUserPromptId) {
      setSelectedUserPromptId(null);
    }
  }, [customPromptsUnlocked, selectedUserPromptId, setSelectedUserPromptId]);

  const dbMethodology = METHODOLOGY_TO_DB[methodology];
  const matchingPrompts = userPrompts.filter(
    (p) => p.methodology === null || p.methodology === dbMethodology,
  );
  const selectedPrompt = matchingPrompts.find((p) => p.id === selectedUserPromptId) ?? null;

  // Symbol search handler — proxied through /market/symbols/search-analysis
  // The returned list is cached in the component so the toolbar button can show
  // the current symbol's name without an extra round-trip.
  const symbolCacheRef = useRef<Record<string, TradingSymbol>>({});

  const handleSearch = useCallback(async (q: string, signal?: AbortSignal): Promise<TradingSymbol[]> => {
    const results = await marketService.searchAnalysisSymbols(q, undefined, signal);
    // Cache each result for later name lookup
    results.forEach((s) => { symbolCacheRef.current[s.id] = s; });
    // If current symbol is in results, update the display info
    const found = results.find((s) => s.id === symbol);
    if (found) setCurrentSymbolInfo(found);
    return results;
  }, [symbol]);

  // When the active symbol changes, try to resolve its name from the cache
  // or fetch it from the API. We first try the curated defaults (empty query)
  // which covers all forex/crypto/indices symbols. If the symbol is not found
  // there (e.g. a stock ticker like MSTR), we do a targeted search so the
  // toolbar button always shows a human-readable display name rather than the
  // raw symbol ID.
  useEffect(() => {
    const cached = symbolCacheRef.current[symbol];
    if (cached) {
      setCurrentSymbolInfo(cached);
      return;
    }

    marketService.searchAnalysisSymbols('').then((results) => {
      results.forEach((s) => { symbolCacheRef.current[s.id] = s; });
      const found = results.find((s) => s.id === symbol);
      if (found) {
        setCurrentSymbolInfo(found);
      } else {
        // Symbol not in curated defaults — search directly (stock tickers, etc.)
        return marketService.searchAnalysisSymbols(symbol).then((searchResults) => {
          searchResults.forEach((s) => { symbolCacheRef.current[s.id] = s; });
          const match = searchResults.find((s) => s.id === symbol);
          if (match) setCurrentSymbolInfo(match);
        });
      }
    }).catch(() => undefined);
  }, [symbol]);

  const handleTimeframeChange = (newTimeframe: string) => {
    if (hasAnalysis && newTimeframe !== timeframe) {
      // Show confirmation if there's an active analysis
      setPendingTimeframe(newTimeframe);
      setShowClearConfirm(true);
    } else {
      setTimeframe(newTimeframe as typeof timeframe);
    }
  };

  const handleConfirmClear = () => {
    if (onClearAnalysis) {
      onClearAnalysis();
    } else {
      clearAnalysis();
    }
    if (pendingTimeframe) {
      setTimeframe(pendingTimeframe as typeof timeframe);
    }
    setShowClearConfirm(false);
    setPendingTimeframe(null);
  };

  const handleCancelClear = () => {
    setShowClearConfirm(false);
    setPendingTimeframe(null);
  };

  const handleClearChart = () => {
    if (onClearAnalysis) {
      onClearAnalysis();
    } else {
      clearAnalysis();
    }
  };

  const handleSymbolSelect = useCallback((symbolId: string) => {
    setSymbol(symbolId);
    setDebugProvider(null);
  }, [setSymbol, setDebugProvider]);

  return (
    <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-bg-secondary border-b border-border-subtle">
      {/* Symbol selector button */}
      <div className="flex flex-col gap-1">
        <button
          onClick={() => setShowSymbolSearch(true)}
          className="flex items-center gap-2 px-2 sm:px-3 py-2 bg-bg-tertiary hover:bg-bg-elevated border border-border-subtle rounded-lg transition-colors min-w-[120px] sm:min-w-[160px]"
        >
          <Search className="w-4 h-4 text-text-muted" />
          <span className="font-medium text-text-primary text-sm sm:text-base">
            {currentSymbolInfo?.name || symbol}
          </span>
          <ChevronDown className="w-4 h-4 text-text-muted ml-auto" />
        </button>
        {/* Delay indicator for indices/ETFs */}
        {currentSymbolInfo?.type === 'indices' && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-warning/20 text-warning border border-warning/30 flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-warning animate-glow-pulse" />
            {t('toolbar.delay')}
          </span>
        )}
      </div>

      {/* Symbol Search Modal */}
      <SymbolSearch
        onSearch={handleSearch}
        currentSymbol={symbol}
        onSelect={handleSymbolSelect}
        isOpen={showSymbolSearch}
        onClose={() => setShowSymbolSearch(false)}
      />

      {/* Timeframe buttons */}
      <div className="flex items-center gap-0.5 sm:gap-1 bg-bg-tertiary rounded-lg p-0.5 sm:p-1 overflow-x-auto">
        {['1m', '5m', '15m', '1h', '4h', '1d'].map((tf) => {
          const isLocked = !allowedTimeframes.includes(tf);
          return (
            <button
              key={tf}
              onClick={() => {
                if (isLocked) {
                  setUpgradeMessage(t('toolbar.locked', { tf: tf.toUpperCase() }));
                  setShowUpgradePrompt(true);
                } else {
                  handleTimeframeChange(tf);
                }
              }}
              className={`
                flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors
                ${isLocked
                  ? 'text-text-muted opacity-50 cursor-not-allowed'
                  : timeframe === tf
                    ? 'bg-accent-primary text-white'
                    : 'text-text-secondary hover:text-text-primary hover:bg-bg-elevated'
                }
              `}
            >
              {tf.toUpperCase()}
              {isLocked && <Lock className="w-3 h-3" />}
            </button>
          );
        })}
      </div>

      {/* Source selector removed — provider is now env-driven server-side. */}

      {/* Spacer */}
      <div className="flex-1 min-w-[8px]" />

      {/* Analysis Overlay Toggle - hidden on mobile */}
      {onToggleSMC && (
        <Button
          variant={showSMC ? 'secondary' : 'ghost'}
          size="sm"
          onClick={onToggleSMC}
          title={showSMC ? t('toolbar.hideOverlay') : t('toolbar.showOverlay')}
          className={`hidden sm:flex ${showSMC ? 'bg-accent-primary/20 text-accent-primary' : ''}`}
        >
          <Layers className="w-4 h-4" />
        </Button>
      )}

      {/* Fit-content / zoom-reset button (D) */}
      {onFitContent && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onFitContent}
          title={t('toolbar.fitContent')}
          className="hidden sm:flex"
        >
          <Maximize2 className="w-4 h-4" />
        </Button>
      )}

      {/* Refresh button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onRefresh}
        disabled={isLoading}
        className="hidden sm:flex"
      >
        <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
      </Button>

      {/* Recent analyses dropdown */}
      <div className="relative">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowHistory(!showHistory)}
          title={t('toolbar.loadRecent')}
        >
          <History className="w-4 h-4" />
        </Button>
      </div>

      {/* History modal - rendered via Portal to escape any container restrictions */}
      {showHistory && recentAnalyses.length > 0 && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-20">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowHistory(false)}
          />
          {/* Modal content */}
          <div className="relative w-80 max-w-[90vw] bg-bg-elevated border border-border-default rounded-lg shadow-xl overflow-hidden">
            <div className="p-3 border-b border-border-subtle flex items-center justify-between">
              <span className="text-sm font-medium text-text-primary">{t('toolbar.recentAnalyses')}</span>
              <button
                onClick={() => setShowHistory(false)}
                className="text-text-muted hover:text-text-primary text-lg leading-none"
              >
                ×
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {recentAnalyses.map((analysis) => (
                <button
                  key={analysis.id}
                  onClick={() => {
                    loadAnalysis(analysis);
                    setShowHistory(false);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-bg-tertiary transition-colors flex items-center gap-3 border-b border-border-subtle last:border-b-0"
                >
                  <span className={`w-3 h-3 rounded-full flex-shrink-0 ${analysis.direction === 'long' ? 'bg-profit' : 'bg-loss'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary">
                      {getSymbolName(analysis.symbol)} • {analysis.timeframe}
                    </div>
                    <div className="text-xs text-text-secondary mt-0.5">
                      {analysis.direction?.toUpperCase()} {analysis.confidence}% • {formatDate(new Date(analysis.createdAt))}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Clear chart button */}
      {hasAnalysis && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearChart}
          title={t('toolbar.clearChart')}
          className="text-text-secondary hover:text-loss"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )}

      {/* Strategy selector — unified: 4 official strategies + (Pro+) the
          user's custom prompts in a second group when any exist. */}
      <div className="relative">
        <button
          onClick={() => setShowStrategySelector(!showStrategySelector)}
          disabled={isAnalyzing}
          className={`
            flex items-center gap-1.5 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium
            bg-bg-tertiary hover:bg-bg-elevated border border-border-subtle rounded-lg transition-colors
            ${isAnalyzing ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          title={t('toolbar.selectStrategy')}
        >
          <Target className="w-3.5 h-3.5 text-accent-primary" />
          {selectedPrompt ? (
            <span className="text-text-primary truncate max-w-[120px] sm:max-w-[200px]">{selectedPrompt.name}</span>
          ) : (
            <span className="text-text-primary">{STRATEGY_ABBREV[methodology]}</span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-text-muted" />
        </button>

        {/* Strategy selector dropdown */}
        {showStrategySelector && createPortal(
          <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-20">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowStrategySelector(false)}
            />
            <div className="relative w-80 max-w-[90vw] bg-bg-elevated border border-border-default rounded-lg shadow-xl overflow-hidden">
              <div className="p-3 border-b border-border-subtle flex items-center justify-between">
                <span className="text-sm font-medium text-text-primary flex items-center gap-2">
                  <Target className="w-4 h-4 text-accent-primary" />
                  {t('toolbar.selectStrategy')}
                </span>
                <button
                  onClick={() => setShowStrategySelector(false)}
                  aria-label={t('common:actions.close')}
                  className="text-text-muted hover:text-text-primary text-lg leading-none"
                >
                  ×
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {/* Official strategies group */}
                <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-text-tertiary border-b border-border-subtle bg-bg-secondary">
                  {t('toolbar.strategySelector.officialGroup')}
                </div>
                {STRATEGY_LIST.map((strategyId) => {
                  const strategyKeys = STRATEGY_KEYS[strategyId];
                  const officialTpl = officialTemplates.find(
                    (tpl) => DB_TO_METHODOLOGY[tpl.strategy] === strategyId
                  );
                  const isSelected = methodology === strategyId && selectedUserPromptId === null;
                  return (
                    <button
                      key={strategyId}
                      onClick={() => {
                        setMethodology(strategyId);
                        setSelectedUserPromptId(null);
                        setShowStrategySelector(false);
                      }}
                      className={`
                        w-full px-4 py-3 text-left hover:bg-bg-tertiary transition-colors
                        flex items-center gap-3 border-b border-border-subtle last:border-b-0
                        ${isSelected ? 'bg-accent-primary/10' : ''}
                      `}
                    >
                      <div className={`
                        w-2 h-2 rounded-full flex-shrink-0
                        ${isSelected ? 'bg-accent-primary' : 'bg-border-subtle'}
                      `} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${isSelected ? 'text-accent-primary' : 'text-text-primary'}`}>
                          {officialTpl?.name ?? t(strategyKeys.nameKey)}
                        </div>
                        <div className="text-xs text-text-secondary mt-0.5">
                          {t(strategyKeys.descKey)}
                        </div>
                      </div>
                    </button>
                  );
                })}

                {/* Custom group — only when the user has any matching custom prompt */}
                {customPromptsUnlocked && matchingPrompts.length > 0 && (
                  <>
                    <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-text-tertiary border-b border-border-subtle bg-bg-secondary">
                      {t('toolbar.strategySelector.customGroup')}
                    </div>
                    {matchingPrompts.map((prompt) => {
                      const isSelected = prompt.id === selectedUserPromptId;
                      return (
                        <button
                          key={prompt.id}
                          onClick={() => {
                            setSelectedUserPromptId(prompt.id);
                            setShowStrategySelector(false);
                          }}
                          className={`
                            w-full px-4 py-3 text-left hover:bg-bg-tertiary transition-colors
                            flex items-center gap-3 border-b border-border-subtle last:border-b-0
                            ${isSelected ? 'bg-accent-primary/10' : ''}
                          `}
                        >
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? 'bg-accent-primary' : 'bg-border-subtle'}`} />
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-medium truncate ${isSelected ? 'text-accent-primary' : 'text-text-primary'}`}>
                              {prompt.name}
                              {prompt.isDefault && <span className="ml-2 text-[10px] text-accent-primary">★</span>}
                            </div>
                            <div className="text-xs text-text-secondary mt-0.5 line-clamp-2">
                              {prompt.content}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}

                {/* Manage link for Pro users (no custom yet) */}
                {customPromptsUnlocked && matchingPrompts.length === 0 && (
                  <div className="px-4 py-3 text-center border-t border-border-subtle">
                    <Link
                      to="/settings"
                      onClick={() => setShowStrategySelector(false)}
                      className="inline-flex items-center gap-1 text-xs text-accent-primary hover:underline"
                    >
                      {t('toolbar.strategySelector.manageLink')}
                    </Link>
                  </div>
                )}

                {/* Upsell row for free users to discover custom prompts */}
                {!customPromptsUnlocked && (
                  <div className="px-4 py-3 text-center border-t border-border-subtle">
                    <button
                      onClick={() => {
                        setShowStrategySelector(false);
                        setUpgradeMessage(t('toolbar.strategySelector.upgradeMessage'));
                        setShowUpgradePrompt(true);
                      }}
                      className="text-xs text-accent-primary hover:underline flex items-center gap-1 mx-auto"
                    >
                      <Lock className="w-3 h-3" />
                      {t('toolbar.strategySelector.lockedTooltip')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* G: Sparse data warning — shown when weekend filter dropped candles and
          what remains is dangerously close to the minimum. */}
      {(() => {
        const dropped = (candlesBeforeFilter ?? 0) - candleCount;
        const sparseThreshold = MIN_CANDLES_FOR_ANALYSIS * 1.5;
        if (dropped > 0 && candleCount < sparseThreshold && candleCount >= MIN_CANDLES_FOR_ANALYSIS) {
          return (
            <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md bg-warning/10 border border-warning/30 text-[11px] text-warning">
              <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{t('toolbar.sparseDataWarning', { dropped })}</span>
            </div>
          );
        }
        return null;
      })()}

      {/* Analyze button — disabled while data still loading or below the
          minimum candle count the API requires. */}
      {(() => {
        const insufficientData = candleCount > 0 && candleCount < MIN_CANDLES_FOR_ANALYSIS;
        const tooltipText = isLoading
          ? t('toolbar.loadingData')
          : insufficientData
            ? t('toolbar.insufficientCandles', {
                min: MIN_CANDLES_FOR_ANALYSIS,
                current: candleCount,
              })
            : undefined;
        return (
          <Button
            variant="primary"
            size="md"
            onClick={onAnalyze}
            loading={isAnalyzing}
            disabled={isAnalyzing || isLoading || insufficientData}
            className="gap-2"
            title={tooltipText}
          >
            <Wand2 className="w-4 h-4" />
            <span>{t('toolbar.analyze')}</span>
          </Button>
        );
      })()}

      {/* Upgrade prompt modal */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        limitType={t('toolbar.featureRestricted')}
        message={upgradeMessage}
      />

      {/* Confirmation modal for clearing analysis */}
      {showClearConfirm && (
        <>
          <div
            data-testid="modal-backdrop"
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={handleCancelClear}
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-80">
            <div className="bg-bg-elevated border border-border-default rounded-xl shadow-2xl overflow-hidden">
              {/* Neon accent line */}
              <div className="h-1 bg-gradient-to-r from-accent-primary via-accent-secondary to-accent-primary" />

              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-warning/20 flex items-center justify-center">
                    <svg className="w-5 h-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-text-primary">
                      {t('toolbar.dialog.clearTitle')}
                    </h3>
                    <p className="text-xs text-text-secondary">
                      {t('toolbar.dialog.timeframeChange')}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-text-secondary mb-5">
                  {t('toolbar.dialog.timeframeBody', { timeframe: pendingTimeframe?.toUpperCase() })}
                </p>

                <div className="flex gap-2">
                  <button
                    onClick={handleCancelClear}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-text-secondary hover:text-text-primary bg-bg-tertiary hover:bg-bg-secondary rounded-lg transition-colors"
                  >
                    {t('toolbar.dialog.cancel')}
                  </button>
                  <button
                    onClick={handleConfirmClear}
                    className="flex-1 px-4 py-2.5 text-sm font-medium bg-accent-primary hover:bg-accent-primary/90 text-white rounded-lg transition-colors shadow-lg shadow-accent-primary/25"
                  >
                    {t('toolbar.dialog.clearAndChange')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
